import { action, computed, observable, makeObservable } from 'mobx';
import isNil from 'lodash/isNil';

import type { LegacyDataParams, LegacyValueParams } from 'chaika-frontend-types/frontend-cms';
import { XmlDataType } from 'chaika-frontend-types/frontend-cms';

import { executeFieldNamesSet } from './constants';
import { assertIsDefined } from '@utils/assertIsDefined';

export class LegacyData {
    @observable
    public readonly id: string;

    @observable
    public readonly label: string;

    @observable
    public readonly tableName: string;

    @observable
    private readonly viewAsTable?: boolean;

    @observable
    private blockValues: Map<string, LegacyValueParams>;

    constructor({ id, label, viewAsTable, tableName, values }: LegacyDataParams & { id: string }) {
        makeObservable(this);

        this.id = id;
        this.label = label;
        this.tableName = tableName ?? '';
        this.viewAsTable = viewAsTable;
        this.blockValues = new Map(this.fixDateValues(values).map(value => [value.fieldName, value]));
    }

    @computed
    public get blockValuesFieldNames(): string[] {
        return Array.from(this.blockValues.values())
            .filter(
                ({ value, fieldName, noResume }) =>
                    !executeFieldNamesSet.has(fieldName.toUpperCase()) &&
                    (typeof value === 'boolean' ? value : true) &&
                    (noResume ? noResume !== '1' : true),
            )
            .map(({ fieldName }) => fieldName);
    }

    @computed
    public get serialized(): LegacyDataParams {
        return {
            label: this.label,
            viewAsTable: this.viewAsTable,
            tableName: this.tableName,
            values: Array.from(this.blockValues.values()),
        };
    }

    public getFilteredBlockValuesFieldNames(executeFieldNames: string[]): string[] {
        const executeNames = new Set(executeFieldNames);
        return Array.from(this.blockValues.values())
            .filter(
                ({ value, fieldName, noResume }) =>
                    !executeFieldNamesSet.has(fieldName.toUpperCase()) &&
                    !executeNames.has(fieldName.toUpperCase()) &&
                    (typeof value === 'boolean' ? value : true) &&
                    (noResume ? noResume !== '1' : true),
            )
            .map(({ fieldName }) => fieldName);
    }

    public hasField(fieldName: string): boolean {
        return this.blockValues.has(fieldName);
    }

    public hasFieldData(fieldName: string): boolean {
        const fieldValue = this.blockValues.get(fieldName);
        return !isNil(fieldValue) && fieldValue.value !== '';
    }

    public getBlockValue(fieldName: string): LegacyValueParams {
        const value = this.blockValues.get(fieldName);
        assertIsDefined(value, `Legacy data block has a value for field ${fieldName}`);

        return value;
    }

    @action
    public updateBlockValue(fieldName: string, value: string | number | boolean): void {
        const blockValue = this.blockValues.get(fieldName);
        assertIsDefined(blockValue, `Legacy field ${fieldName} is updated only when it has a value`);

        blockValue.value = value;
    }

    private fixDateValues(values: LegacyValueParams[]): LegacyValueParams[] {
        return values.map(value => (value.type === XmlDataType.DateTime ? this.fixDateValue(value) : value));
    }

    private fixDateValue({ value, ...params }: LegacyValueParams): LegacyValueParams {
        const formattedDateRegExp = /\d\d.\d\d.\d\d\d\d/;

        const fixedValue = formattedDateRegExp.test(value as string) ? this.fixDate(value as string) : value;

        return {
            value: fixedValue,
            ...params,
        };
    }

    private fixDate(date: string): string {
        let fixedDate = '';

        if (date.indexOf(' ') !== -1) {
            const isoDate = date.slice(0, date.indexOf(' ')).split('.').reverse().join('-');
            const isoTime = date.slice(date.indexOf(' ') + 1);
            fixedDate = `${isoDate} ${isoTime}`;
        } else {
            fixedDate = date.split('.').reverse().join('-');
        }

        return fixedDate;
    }
}
